import { useCallback, useEffect, useState } from 'react'
import Papa from 'papaparse'
import type { CensusRecord } from '../types/census'
import { normalizeText, onlyDigits } from '../utils/text'

const CENSUS_CSV_URL = 'https://cdn.magicpatterns.com/uploads/gnpcXgHAvuoS6QoKC2kbUm/Censo_Electoral_31082026.csv'

type RawRow = Record<string, string | undefined>

type CensusState =
  | { status: 'loading'; records: CensusRecord[] }
  | { status: 'error'; records: CensusRecord[] }
  | { status: 'ready'; records: CensusRecord[] }

/** Finds a column by a normalized prefix, tolerant to accents / typos in headers */
function pick(row: RawRow, prefix: string): string {
  const key = Object.keys(row).find((k) => normalizeText(k).startsWith(prefix))
  return key ? (row[key] ?? '').trim() : ''
}

function toRecord(row: RawRow, index: number): CensusRecord | null {
  const businessName = pick(row, 'razon social')
  if (!businessName) return null
  const nit = pick(row, 'nit')
  const registration = pick(row, 'matricula')
  const email = pick(row, 'correo')
  // Header has a typo ("NOMPRE"), so match on the stable part
  const legalRep = pick(row, 'nomp') || pick(row, 'nomb')
  return {
    id: pick(row, 'id') || String(index + 1),
    personType: pick(row, 'tipo de persona'),
    businessName,
    registration,
    nit,
    email,
    legalRep,
    searchText: normalizeText(`${businessName} ${legalRep} ${email}`),
    searchDigits: `${onlyDigits(nit)} ${onlyDigits(registration)}`,
  }
}

export function useCensus() {
  const [state, setState] = useState<CensusState>({ status: 'loading', records: [] })

  const load = useCallback(() => {
    setState({ status: 'loading', records: [] })
    Papa.parse<RawRow>(CENSUS_CSV_URL, {
      download: true,
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const records = result.data
          .map(toRecord)
          .filter((r): r is CensusRecord => r !== null)
          .sort((a, b) => a.businessName.localeCompare(b.businessName, 'es'))
        setState({ status: records.length ? 'ready' : 'error', records })
      },
      error: () => setState({ status: 'error', records: [] }),
    })
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return { ...state, retry: load }
}
